import React from 'react';

import type { FC } from 'react';
import type { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import type { CarouselThumbnailConfig } from '../types';

import { cn } from '../../../utils/cn/cn';

export type CarouselThumbNavType = {
  emblaMain: EmblaCarouselType;
  selectedIndex: number;
  thumbnailConfig: CarouselThumbnailConfig;
  className?: string;
};

export const CarouselThumbNav: FC<CarouselThumbNavType> = ({
  emblaMain,
  selectedIndex,
  thumbnailConfig,
  className,
}) => {
  // Thumbnail Carousel Settings
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  // Thumbnail Carousel Event Handler
  const onThumbClick = (index: number) => emblaMain.scrollTo(index);

  // init everything
  React.useEffect(() => {
    emblaMain.on('select', () => {
      if (emblaThumbs?.scrollTo) {
        emblaThumbs.scrollTo(emblaMain.selectedScrollSnap());
      }
    });
  }, [emblaMain, emblaThumbs]);

  const { thumbnails, gap = 1.25, visibleSlides = 'auto' } = thumbnailConfig;

  return (
    <div className={cn('relative', className)}>
      <div
        ref={thumbViewportRef}
        data-index={selectedIndex}
        className="h-full w-full overflow-hidden"
      >
        <div
          className="flex h-full"
          style={{ gap: typeof gap === 'number' ? `${gap * 0.5}rem` : gap }}
        >
          {thumbnails.map((thumb, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="relative min-w-0 *:block *:w-full"
              style={{
                flex: `0 0 ${
                  typeof visibleSlides === 'number'
                    ? `calc((100% / ${visibleSlides}) - (((${visibleSlides} - 1) / ${visibleSlides}) * ${
                        typeof gap === 'number' ? `${gap * 0.5}rem` : gap
                      }))`
                    : visibleSlides
                }`,
              }}
            >
              <div
                role="button"
                onClick={() => onThumbClick(index)}
                className={cn(
                  `${index === selectedIndex ? 'opacity-100' : 'opacity-20'} relative block h-auto w-full cursor-pointer touch-manipulation overflow-hidden transition-opacity duration-200 ease-out`,
                  typeof thumbnailConfig.thumbnailBoxStyles === 'string' &&
                    thumbnailConfig.thumbnailBoxStyles !== ''
                    ? thumbnailConfig.thumbnailBoxStyles
                    : '',
                )}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onThumbClick(index);
                  }
                }}
              >
                {thumb}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
